import { $http } from '../request'

const API_ROOT = '/promotion/v1'
const STRICT_RESPONSE = { rejectBusinessError: true } as const

export type EventPreorderActivityStatus = 'PREPARING' | 'ON_SALE'
export type EventPreorderVisibilityStatus = 'NOT_STARTED' | 'VISIBLE' | 'ENDED'
export type EventPreorderReservationStatus = 'ACTIVE' | 'CANCELLED'
export type EventPreorderFollowUpStatus = 'PENDING' | 'PURCHASED'
export type EventPreorderCouponClaimStatus = 'UNCLAIMED' | 'CLAIMED'
export type EventPreorderStockStatus = 'IN_STOCK' | 'SOLD_OUT' | 'UNAVAILABLE'

export interface EventPreorderActivity {
  id: number
  regionCode: string
  code: string
  name: string
  pageSlug: string
  pagePath: string
  saleTime: string
  displayStartTime: string
  displayEndTime: string
  liveEmbedCode: string | null
  status: EventPreorderActivityStatus
  visibilityStatus: EventPreorderVisibilityStatus
  isVisible: boolean
  createdTime: string
  updatedTime: string
}

export interface EventPreorderProduct {
  id: number
  activityId: number
  name: string
  imageUrl: string
  estimatedPriceMin: number
  estimatedPriceMax: number
  detailUrl: string
  voucherGenId: number
  sortOrder: number
  reservationCount: number
  reservationCountDisplay: string | null
  isReserved: boolean
  reservationClosed: boolean
  voucherName: string | null
  voucherAmount: number | null
  voucherAvailable: boolean | null
  createdTime: string
  updatedTime: string
}

export interface EventPreorderReservation {
  id: number
  productId: number
  accountId: number
  phone: string
  status: EventPreorderReservationStatus
  followUpStatus: EventPreorderFollowUpStatus
  couponClaimStatus: EventPreorderCouponClaimStatus
  voucherId: number | null
  purchasedListingId: number | null
  reservedTime: string
  cancelledTime: string | null
  couponClaimedTime: string | null
  purchasedTime: string | null
  productName: string
  activityId: number
  saleTime: string
  canBuy: boolean
}

export interface EventPreorderPageData {
  activity: EventPreorderActivity
  products: EventPreorderProduct[]
  myReservations: EventPreorderReservation[]
  unavailableMessage: string | null
}

export interface EventPreorderTimedData<T> {
  data: T
  timestamp: number
}

interface EventPreorderTimestampResponse<T> extends IBaseResponse<T> {
  timestamp: number
}

export interface EventPreorderAvailableListing {
  id: number
  productId: number
  listingId: number
  isEnabled: boolean
  title: string | null
  storeId: number | null
  storeName: string | null
  minSalePrice: number | null
  stockStatus: EventPreorderStockStatus
  selectable: boolean
}

export const getEventPreorderPage = (
  pageSlug: string,
  params: { region_code?: string; account_id?: number } = {}
) => $http.get({
  url: `${API_ROOT}/event-preorder-pages/${encodeURIComponent(pageSlug)}`,
  params,
  server: false,
  ...STRICT_RESPONSE
}).then((response: EventPreorderTimestampResponse<EventPreorderPageData>): EventPreorderTimedData<EventPreorderPageData> => ({
  data: response.data,
  timestamp: response.timestamp
}))

export const createEventPreorderReservation = (
  productId: number,
  body: { accountId: number; phone: string }
) => $http.post({
  url: `${API_ROOT}/event-preorder-products/${productId}/reservations`,
  body,
  loading: true,
  ...STRICT_RESPONSE
}).then((response: IBaseResponse<EventPreorderReservation>) => response.data)

export const cancelEventPreorderReservation = (
  reservationId: number,
  accountId: number
) => $http.delete({
  url: `${API_ROOT}/event-preorder-reservations/${reservationId}`,
  params: { account_id: accountId },
  loading: true,
  ...STRICT_RESPONSE
}).then((response: IBaseResponse<EventPreorderReservation>) => response.data)

export const getMyEventPreorderReservations = (
  activityId: number,
  accountId: number
) => $http.get({
  url: `${API_ROOT}/event-preorders/${activityId}/my-reservations`,
  params: { account_id: accountId },
  server: false,
  ...STRICT_RESPONSE
}).then((response: EventPreorderTimestampResponse<EventPreorderReservation[]>): EventPreorderTimedData<EventPreorderReservation[]> => ({
  data: response.data,
  timestamp: response.timestamp
}))

export const getEventPreorderAvailableListings = (
  productId: number,
  accountId: number
) => $http.get({
  url: `${API_ROOT}/event-preorder-products/${productId}/available-listings`,
  params: { account_id: accountId },
  server: false,
  ...STRICT_RESPONSE
}).then((response: IBaseResponse<EventPreorderAvailableListing[]>) => response.data)
